import { Head, Link } from "@inertiajs/react"
import PublicLayout from "@/components/public/layout"
import { ScrollReveal } from "@/components/ui/scroll-reveal"
import { VideoPhone } from "@/components/public/video-phone"
import { BreadcrumbSchema } from "@/components/public/breadcrumb-schema"
import { RelatedPages } from "@/components/public/related-pages"
import { relatedFor } from "@/data/related-pages"
import type { SeoMeta } from "@/types"

/* ------------------------------------------------------------------ */
/*  Data                                                               */
/* ------------------------------------------------------------------ */

const STEPS = [
  {
    number: "1",
    title: "Copy the OnTheMarket URL",
    description:
      "Find your listing on OnTheMarket and copy the page URL. Works with sales, lettings, and new homes.",
  },
  {
    number: "2",
    title: "QuiteRight imports the listing",
    description:
      "Photos, price, address, property type, bedrooms, and key features are pulled in automatically. No manual entry required.",
  },
  {
    number: "3",
    title: "Pick a template and go live",
    description:
      "Choose Prestige, Modern, Classic, or Bold. QuiteRight handles the script, voiceover, music, and branding. Your video is ready in 60 seconds.",
  },
]

const FEATURES = [
  {
    title: "OnTheMarket data import",
    description:
      "Every detail from the listing page is extracted automatically: photos, price, address, bedrooms, bathrooms, and the property description.",
  },
  {
    title: "Works alongside Rightmove and Zoopla",
    description:
      "QuiteRight supports all three major UK portals. Create videos from Rightmove, Zoopla, and OnTheMarket URLs using the same simple process.",
  },
  {
    title: "AI-generated property scripts",
    description:
      "The listing description is transformed into a concise, engaging video script. No copywriting needed. The voiceover is generated automatically.",
  },
  {
    title: "Multi-platform video formats",
    description:
      "Every video is rendered for Instagram Reels, TikTok, YouTube Shorts, Facebook, and LinkedIn. Portrait, landscape, and square.",
  },
  {
    title: "Agency branding built in",
    description:
      "Your logo, name, and contact details appear on every video automatically. Set it once; it applies to every video your team creates.",
  },
  {
    title: "Scale without limits",
    description:
      "Whether you list 5 or 50 properties on OnTheMarket each month, QuiteRight handles them all. Each video takes about 60 seconds.",
  },
]

const STATS = [
  { stat: "60s", label: "from OnTheMarket URL to video" },
  { stat: "3", label: "UK portals supported" },
  { stat: "50", label: "videos per month on the Managed plan" },
  { stat: "0", label: "design skills required" },
]

type FaqItem = { q: string; a: string }

const FAQ: FaqItem[] = [
  {
    q: "Does QuiteRight work with OnTheMarket listings?",
    a: "Yes. QuiteRight supports OnTheMarket alongside Rightmove and Zoopla. Paste any OnTheMarket listing URL and QuiteRight imports the photos, price, and details to create a branded video ad in 60 seconds.",
  },
  {
    q: "Can I create videos for OnTheMarket lettings?",
    a: "Yes. Rental listings on OnTheMarket work exactly the same as sales. The generated script and voiceover adapt to letting-specific language automatically, including rent, deposit, and availability.",
  },
  {
    q: "Do I need to be an OnTheMarket member to use QuiteRight?",
    a: "You need a listing on OnTheMarket to create a video from it, but you do not need any special OnTheMarket account or API access. QuiteRight works with the publicly accessible listing page.",
  },
  {
    q: "Can I use videos from OnTheMarket listings on social media?",
    a: "Yes. The whole point is social media distribution. Every video comes ready for Instagram Reels, TikTok, YouTube Shorts, Facebook, and LinkedIn. Download or publish directly from QuiteRight.",
  },
  {
    q: "What if my listing is on multiple portals?",
    a: "You only need to create the video once. Use whichever portal URL is most convenient. The result is the same branded video regardless of whether you paste a Rightmove, Zoopla, or OnTheMarket URL.",
  },
  {
    q: "How does OnTheMarket's new homes section work with QuiteRight?",
    a: "New homes and development listings on OnTheMarket work like any other listing. Paste the URL and QuiteRight extracts the available photos and details. For developments with multiple plot types, create a video per plot for maximum reach.",
  },
]

/* ------------------------------------------------------------------ */
/*  Components                                                         */
/* ------------------------------------------------------------------ */

function FaqAccordion({ items }: { items: FaqItem[] }) {
  return (
    <dl className="divide-y divide-stone">
      {items.map((item, i) => (
        <details key={i} className="group">
          <summary className="flex cursor-pointer items-start justify-between gap-4 py-5 text-left [&::-webkit-details-marker]:hidden">
            <dt className="text-sm font-semibold leading-relaxed text-charcoal">
              {item.q}
            </dt>
            <span className="mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full bg-stone text-slate transition-transform duration-200 group-open:rotate-45">
              <svg
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                className="size-3"
              >
                <path d="M8 3v10M3 8h10" />
              </svg>
            </span>
          </summary>
          <dd className="pb-5 text-sm leading-relaxed text-slate">{item.a}</dd>
        </details>
      ))}
    </dl>
  )
}

/* ------------------------------------------------------------------ */
/*  Page                                                               */
/* ------------------------------------------------------------------ */

export default function IntegrationOnTheMarket({ meta }: { meta: SeoMeta }) {
  return (
    <PublicLayout>
      <Head>
        <title>{meta.title}</title>
        <meta name="description" content={meta.description} />
        <link rel="canonical" href={meta.canonical} />

        <meta property="og:title" content={meta.title} />
        <meta property="og:description" content={meta.description} />
        <meta property="og:url" content={meta.canonical} />
        <meta property="og:type" content="article" />
        <meta property="og:image" content={meta.image} />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={meta.title} />
        <meta name="twitter:description" content={meta.description} />
        <meta name="twitter:image" content={meta.image} />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: "How to Create Video Ads from OnTheMarket Listings",
            description: meta.description,
            publisher: {
              "@type": "Organization",
              name: "QuiteRight",
              url: "https://www.quiteright.co.uk",
              logo: {
                "@type": "ImageObject",
                url: "https://www.quiteright.co.uk/qr-logo-wordmark-red.png",
              },
            },
            datePublished: "2026-02-25",
            dateModified: "2026-02-25",
          })}
        </script>

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: FAQ.map((faq) => ({
              "@type": "Question",
              name: faq.q,
              acceptedAnswer: { "@type": "Answer", text: faq.a },
            })),
          })}
        </script>

        <BreadcrumbSchema
          canonical={meta.canonical}
          items={[
            { name: "Integrations", path: "/integrations" },
            { name: "OnTheMarket" },
          ]}
        />
      </Head>

      {/* Hero */}
      <section className="px-6 py-24 md:py-32">
        <div className="mx-auto max-w-6xl">
          <div className="grid items-center gap-12 md:grid-cols-2">
            <div>
              <p className="text-xs font-medium uppercase tracking-[0.2em] text-red">
                Integration
              </p>
              <h1 className="mt-4 font-display text-4xl font-bold tracking-tight text-charcoal sm:text-5xl">
                Turn OnTheMarket listings into video ads
                <span className="text-red">.</span>
              </h1>
              <p className="mt-6 max-w-lg text-lg text-slate">
                Paste any OnTheMarket URL. QuiteRight imports the photos,
                price, and property details, then creates a professional video
                ad with voiceover and music in 60 seconds.
              </p>
              <div className="mt-8 flex flex-col gap-4 sm:flex-row">
                <Link
                  href="/sign-up"
                  className="inline-flex items-center justify-center rounded-md bg-red px-6 py-3 text-base font-medium text-white transition-colors hover:bg-red/90"
                >
                  Start your free trial
                </Link>
                <Link
                  href="/demo"
                  className="inline-flex items-center justify-center rounded-md border border-stone bg-white px-6 py-3 text-base font-medium text-charcoal transition-colors hover:bg-stone"
                >
                  Book a demo
                </Link>
              </div>
            </div>
            <div className="relative flex justify-center">
              <VideoPhone />
              <div className="absolute -bottom-4 -left-4 rounded-lg bg-charcoal px-4 py-2.5 shadow-md md:-left-2">
                <p className="text-xs font-medium text-warm-grey">All 3 UK portals</p>
                <p className="font-display text-xl font-bold text-white">supported</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="border-t border-stone bg-cream px-6 py-20">
        <div className="mx-auto max-w-4xl">
          <ScrollReveal>
            <p className="text-center text-xs font-medium uppercase tracking-[0.2em] text-red">
              How it works
            </p>
            <h2 className="mt-4 text-center font-display text-3xl font-bold text-charcoal md:text-4xl">
              One URL. One click. One video
              <span className="text-red">.</span>
            </h2>
          </ScrollReveal>

          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {STEPS.map((step) => (
              <ScrollReveal key={step.number}>
                <div className="rounded-xl border border-stone bg-white p-6">
                  <span className="flex size-10 items-center justify-center rounded-full bg-red font-display text-lg font-bold text-white">
                    {step.number}
                  </span>
                  <h3 className="mt-4 font-display text-lg font-bold text-charcoal">
                    {step.title}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate">
                    {step.description}
                  </p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="border-t border-stone px-6 py-20">
        <div className="mx-auto max-w-5xl">
          <ScrollReveal>
            <p className="text-center text-xs font-medium uppercase tracking-[0.2em] text-red">
              What you get
            </p>
            <h2 className="mt-4 text-center font-display text-3xl font-bold text-charcoal md:text-4xl">
              OnTheMarket listings, amplified
              <span className="text-red">.</span>
            </h2>
          </ScrollReveal>

          <ScrollReveal stagger className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {FEATURES.map((feature, i) => (
              <div key={i} className="rounded-xl border border-stone bg-white p-6">
                <h3 className="font-display text-base font-bold text-charcoal">
                  {feature.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-slate">
                  {feature.description}
                </p>
              </div>
            ))}
          </ScrollReveal>
        </div>
      </section>

      {/* Stats */}
      <section className="border-t border-stone bg-charcoal px-6 py-16">
        <ScrollReveal
          stagger
          className="mx-auto grid max-w-5xl grid-cols-2 gap-px overflow-hidden rounded-xl border border-slate/20 bg-slate/20 sm:grid-cols-4"
        >
          {STATS.map((item) => (
            <div key={item.stat} className="bg-charcoal px-5 py-6 text-center">
              <p className="font-display text-2xl font-bold text-cream md:text-3xl">
                {item.stat}
              </p>
              <p className="mt-2 text-xs leading-snug text-warm-grey">{item.label}</p>
            </div>
          ))}
        </ScrollReveal>
      </section>

      {/* FAQ */}
      <section className="border-t border-stone px-6 py-20">
        <div className="mx-auto max-w-3xl">
          <ScrollReveal>
            <p className="text-center text-xs font-medium uppercase tracking-[0.2em] text-red">
              FAQ
            </p>
            <h2 className="mt-4 text-center font-display text-3xl font-bold text-charcoal md:text-4xl">
              Common questions
              <span className="text-red">.</span>
            </h2>
          </ScrollReveal>

          <div className="mt-12">
            <ScrollReveal>
              <FaqAccordion items={FAQ} />
            </ScrollReveal>
          </div>
        </div>
      </section>

      <RelatedPages links={relatedFor("integrations/onthemarket")} tone="white" />

      {/* CTA */}
      <section className="border-t border-stone bg-cream px-6 py-24">
        <div className="mx-auto max-w-3xl text-center">
          <ScrollReveal>
            <h2 className="font-display text-3xl font-bold text-charcoal md:text-4xl">
              Every portal. Every listing. Every platform
              <span className="text-red">.</span>
            </h2>
            <p className="mx-auto mt-4 max-w-xl text-lg text-slate">
              Rightmove, Zoopla, OnTheMarket. Paste the URL, pick a template,
              and your video is live. Try QuiteRight free for 14 days.
            </p>
            <div className="mt-8 flex flex-col items-center justify-center gap-4 sm:flex-row">
              <Link
                href="/sign-up"
                className="inline-flex w-full items-center justify-center rounded-md bg-red px-6 py-3 text-base font-medium text-white transition-colors hover:bg-red/90 sm:w-auto"
              >
                Start your free trial
              </Link>
              <Link
                href="/demo"
                className="inline-flex w-full items-center justify-center rounded-md border border-stone bg-white px-6 py-3 text-base font-medium text-charcoal transition-colors hover:bg-stone sm:w-auto"
              >
                Book a demo
              </Link>
            </div>
          </ScrollReveal>
        </div>
      </section>
    </PublicLayout>
  )
}
